import { Calendar, Compass, MessageCircle, Umbrella } from "lucide-react";
import { useClientTranslations } from "../../lib/i18n/client";
import type { JourneyStep } from "../../types/journey";
import styles from "./StepHighlights.module.css";

interface StepHighlightsProps {
	step: JourneyStep | null;
}

function computeCurrentDay(step: JourneyStep, now: Date): number | null {
	if (!step.estimatedStartDate || !step.highlight.estimatedLengthDays) {
		return null;
	}
	const start = new Date(step.estimatedStartDate);
	if (Number.isNaN(start.getTime())) return null;
	const msPerDay = 24 * 60 * 60 * 1000;
	const dayIdx = Math.floor((now.getTime() - start.getTime()) / msPerDay) + 1;
	const total = step.highlight.estimatedLengthDays;
	if (step.status === "completed") return total;
	if (step.status === "future") return 0;
	return Math.max(1, Math.min(dayIdx, total));
}

export function StepHighlights({ step }: StepHighlightsProps) {
	const { t } = useClientTranslations("journey");

	if (!step) return null;

	const { highlight } = step;
	const total = highlight.estimatedLengthDays;
	const day = computeCurrentDay(step, new Date());
	let dayLabel: string;
	if (highlight.currentDayLabel) {
		// V2 fixture: use the patient label template, replacing [N] with computed day.
		dayLabel = highlight.currentDayLabel.replace(/\[N\]/g, String(day ?? 1));
	} else if (day !== null && total) {
		dayLabel = `${t("stepHighlights.day", { defaultValue: "Day" })} ${day} ${t("stepHighlights.of", { defaultValue: "of" })} ~${total}`;
	} else {
		dayLabel = highlight.dayCount;
	}

	return (
		<section
			className={styles.container}
			data-step-content="highlights"
			aria-labelledby="step-highlights-heading"
		>
			<h2 id="step-highlights-heading" className={styles.heading}>
				{t("stepHighlights.heading")}
			</h2>

			<div className={styles.cards}>
				<div className={styles.card}>
					<div className={styles.row}>
						<Calendar
							className={styles.icon}
							strokeWidth={1.5}
							aria-label={t("stepHighlights.iconLabels.calendar")}
						/>
						<p className={styles.text}>
							<span className={styles.bold}>{dayLabel}</span>
						</p>
					</div>

					<div className={styles.row}>
						<Compass
							className={styles.icon}
							strokeWidth={1.5}
							aria-label={t("stepHighlights.iconLabels.compass")}
						/>
						<p className={styles.text}>
							<span className={styles.bold}>
								{t("stepHighlights.rowLabels.milestone", {
									defaultValue: "Milestone:",
								})}
							</span>{" "}
							<span>{highlight.milestone ?? highlight.monitoringSchedule}</span>
						</p>
					</div>

					<div className={styles.row}>
						<MessageCircle
							className={styles.icon}
							strokeWidth={1.5}
							aria-label={t("stepHighlights.iconLabels.messageCircle")}
						/>
						<p className={styles.text}>
							<span className={styles.bold}>
								{t("stepHighlights.rowLabels.staff")}
							</span>{" "}
							<span>{highlight.staffInfo}</span>
						</p>
					</div>
				</div>

				<div className={styles.cardHeadsUp}>
					<div className={styles.rowHeadsUp}>
						<Umbrella
							className={styles.icon}
							strokeWidth={1.5}
							aria-label={t("stepHighlights.iconLabels.umbrella")}
						/>
						<p className={styles.text}>
							<span className={styles.bold}>
								{t("stepHighlights.rowLabels.headsUp")}
							</span>{" "}
							<span>{highlight.headsUpNote}</span>
						</p>
					</div>
				</div>
			</div>
		</section>
	);
}
